import React from 'react';
import PropTypes, { func } from 'prop-types';
import { Sparkle20Regular } from '@fluentui/react-icons';
import {
  mergeClasses,
  Skeleton,
  SkeletonItem,
  Text,
} from '@fluentui/react-components';
import {
  InteractionTag,
  InteractionTagPrimary,
  TagGroup,
} from '@fluentui/react-tags';

import { useSuggestionsStyles } from './styles';

const loadingSkeletonItemCount = 5;

const SuggestionLoadingSkeleton = () => {
  const classes = useSuggestionsStyles();
  return (
    <Skeleton animation="wave" className={classes.loadingContainer}>
      {Array.from({ length: loadingSkeletonItemCount }).map((_, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <SkeletonItem size={32} key={index} />
      ))}
    </Skeleton>
  );
};

const SuggestionPanel = ({
  title,
  loading,
  className,
  suggestions,
}) => {
  const classes = useSuggestionsStyles();

  if (!loading && (!suggestions || suggestions.length === 0)) {
    return null;
  }

  return (
    <div className={mergeClasses(className, classes.panel)}>
      <Sparkle20Regular />
      <div>
        <Text size={300} weight={400} className={`${className}-title`}>
          {title}
        </Text>
        {loading ? (
          <SuggestionLoadingSkeleton />
        ) : (
          <TagGroup
            aria-label={title}
            className={mergeClasses(
              'suggestions-panel-body',
              classes.panelBody
            )}
          >
            {suggestions.map(suggestion => (
              <InteractionTag key={suggestion} appearance="outline">
                <InteractionTagPrimary>{suggestion}</InteractionTagPrimary>
              </InteractionTag>
            ))}
          </TagGroup>
        )}
      </div>
    </div>
  );
};

SuggestionPanel.propTypes = {
  className: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  suggestions: PropTypes.arrayOf(PropTypes.string).isRequired,
};

SuggestionPanel.defaultProps = {
  className: '',
};

export const Suggestions = ({
  i18n,
  loading,
  descriptionSuggestions,
  shortHeadlinesSuggestions,
  longHeadlinesSuggestions,
}) => {
  const classes = useSuggestionsStyles();
  return (
    <section
      className={mergeClasses(
        'ads-studio-brand-kit-brand-voice-suggestions',
        classes.container
      )}
    >
      <SuggestionPanel
        loading={loading}
        className="short-headlines-suggestions"
        suggestions={shortHeadlinesSuggestions}
        title={i18n.getString(_TL_('Examples of how your short headlines might look like in ads'))}
      />
      <SuggestionPanel
        loading={loading}
        className="long-headlines-suggestions"
        suggestions={longHeadlinesSuggestions}
        title={i18n.getString(_TL_('Examples of how your long headlines might look like in ads'))}
      />
      <SuggestionPanel
        loading={loading}
        className="description-suggestions"
        suggestions={descriptionSuggestions}
        title={i18n.getString(_TL_('Examples of how your descriptions might look like in ads'))}
      />
    </section>
  );
};

Suggestions.propTypes = {
  loading: PropTypes.bool,
  i18n: PropTypes.shape({ getString: func }).isRequired,
  shortHeadlinesSuggestions: PropTypes.arrayOf(PropTypes.string),
  longHeadlinesSuggestions: PropTypes.arrayOf(PropTypes.string),
  descriptionSuggestions: PropTypes.arrayOf(PropTypes.string),
};

Suggestions.defaultProps = {
  loading: false,
  shortHeadlinesSuggestions: [],
  longHeadlinesSuggestions: [],
  descriptionSuggestions: [],
};
